import { z } from "zod";
import { eventFamilies, platformSlugs } from "./types";

const MetadataValueSchema = z.union([z.string(), z.number(), z.boolean(), z.null()]);
const sensitiveMetadataMarkers = [
  "email",
  "phone",
  "telephone",
  "address",
  "authorization",
  "accesstoken",
  "refreshtoken",
  "clientsecret",
  "password",
  "creditcard",
  "cardnumber",
  "capabilitytoken",
  "contexttoken",
  "oauth",
] as const;
const emailLike = /^[^\s@]+@[^\s@]+\.[^\s@]+$/u;
const postgresUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/iu;
const PostgresUuidSchema = z.string().regex(postgresUuid, "Invalid PostgreSQL UUID");

function normalizedMetadataKey(value: string) {
  return value.toLowerCase().replaceAll(/[^a-z0-9]/gu, "");
}

const MetadataSchema = z
  .record(z.string().min(1).max(80), MetadataValueSchema)
  .superRefine((metadata, context) => {
    for (const [key, value] of Object.entries(metadata)) {
      const normalized = normalizedMetadataKey(key);
      if (sensitiveMetadataMarkers.some((marker) => normalized.includes(marker))) {
        context.addIssue({
          code: "custom",
          message: "Sensitive metadata keys are not allowed",
          path: [key],
        });
      }
      if (typeof value === "string" && (emailLike.test(value) || /^Bearer\s+/iu.test(value))) {
        context.addIssue({
          code: "custom",
          message: "Sensitive metadata values are not allowed",
          path: [key],
        });
      }
    }
  });

export const SeedEventEnvelopeSchema = z.object({
  schemaVersion: z.literal(1),
  seedId: PostgresUuidSchema,
  deploymentId: PostgresUuidSchema,
  platform: z.enum(platformSlugs),
  tenantId: z
    .string()
    .min(1)
    .max(160)
    .refine((value) => !emailLike.test(value), {
      message: "Tenant id must not be an email address",
    }),
  eventId: z.string().min(8).max(160),
  eventName: z.string().regex(/^[a-z][a-z0-9_]{2,79}$/u),
  eventFamily: z.enum(eventFamilies),
  occurredAt: z.string().datetime({ offset: true }),
  value: z.number().finite().optional(),
  metadata: MetadataSchema.optional(),
});

export const EventBatchSchema = z
  .object({
    schemaVersion: z.literal(1),
    events: z.array(SeedEventEnvelopeSchema).min(1).max(100),
  })
  .superRefine((batch, context) => {
    const seen = new Set<string>();
    batch.events.forEach((event, index) => {
      if (seen.has(event.eventId)) {
        context.addIssue({
          code: "custom",
          message: "Duplicate eventId inside one batch",
          path: ["events", index, "eventId"],
        });
      }
      seen.add(event.eventId);
    });
  });
